import React, { useState, useEffect } from "react"
import { Box, Button, Flex, Stack, Alert, AlertIcon, AlertTitle, AlertDescription, Text, useTheme } from "@chakra-ui/react"
import { Link as RouterLink, useSearchParams } from "react-router-dom"
import OrdersDashboard from "./OrdersDashboard"
import StoreComments from "./StoreComments"
import ImportLog from "./ImportLog"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

/**
 * Orders page (masters only — the sidebar entry is adminOnly and the backend mount is
 * requireMaster). Three views: the PO ledger for the selected supplier, the per-store
 * comments for that supplier, and the import log. Files are imported by the hourly PnP
 * EDI pickup or from Imports -> PNP -> Orders; both link here as /orders?file=<id> so the
 * ledger opens on exactly that import. docs/michael/pnp-orders/TASK-orders.md
 */
const TABS = [
  { key: "orders", label: "Orders" },
  { key: "comments", label: "Store Comments" },
  { key: "files", label: "Import Log" },
]

const Orders = () => {
  const [searchParams] = useSearchParams()
  const fileParam = searchParams.get("file")
  const supplierParam = searchParams.get("supplier")
  const [activeTab, setActiveTab] = useState("orders")
  const { isMaster, selectedSupplier, setSelectedSupplier, user_supplier_access } = useSupplierRetailer()
  const theme = useTheme()

  // arriving with ?file=<id>&supplier=<code> (from Imports or the Import Log) always lands on the
  // ledger, switched to the supplier that file belongs to
  useEffect(() => { if (fileParam) setActiveTab("orders") }, [fileParam])
  useEffect(() => {
    if (!supplierParam || supplierParam === selectedSupplier) return
    if ((user_supplier_access || []).some((s) => s.value === supplierParam)) setSelectedSupplier(supplierParam)
  }, [supplierParam, selectedSupplier, user_supplier_access, setSelectedSupplier])

  if (!isMaster) {
    return (
      <Box minH="100vh" p={4}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>This page is available to master administrators only.</AlertDescription>
        </Alert>
      </Box>
    )
  }

  return (
    <Box mt={16}>
      <Flex justify="center" mb={2}>
        <Stack direction="row" spacing={4}>
          {TABS.map((t) => (
            <Button key={t.key} onClick={() => setActiveTab(t.key)} variant={activeTab === t.key ? "solid" : "outline"} borderColor={activeTab === t.key ? theme.colors.primary1 : "transparent"} borderWidth="2px">
              {t.label}
            </Button>
          ))}
        </Stack>
      </Flex>
      <Text textAlign="center" fontSize="xs" color="gray.500" mb={3}>
        {selectedSupplier ? `Supplier: ${selectedSupplier} · ` : ""}
        New orders files: hourly from the PnP EDI inbox, or <RouterLink to="/imports" style={{ textDecoration: "underline" }}>Imports → PNP → Orders</RouterLink>
      </Text>
      <Box px={4}>
        {activeTab === "orders" && <OrdersDashboard initialFileId={fileParam} />}
        {activeTab === "comments" && <StoreComments />}
        {activeTab === "files" && <ImportLog />}
      </Box>
    </Box>
  )
}

export default Orders
